import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { pluralize } from '@/utils/pluralization-util'
import { byDesign } from '@/infra/design'
import imgAddToCalendarUrl from '@/assets/misc/img_add-to-calendar.svg'
import CalendarLaunchIllustration from '@/components/SpotIllustration/CalendarLaunchIllustration'

export interface RestoreToWorkspaceDialogProps {
  showing: boolean
  onClose: () => void
  /**
   * The single user being restored, or `null` when restoring a selection.
   */
  selectedUserName: string | null
  selectedUserCount: number
  workspaceName: string
  onRestore: () => void
  isRestorePending: boolean
}

export function RestoreToWorkspaceDialog({
  showing,
  onClose,
  selectedUserName,
  selectedUserCount,
  workspaceName,
  onRestore,
  isRestorePending,
}: RestoreToWorkspaceDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isRestorePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgAddToCalendarUrl}
            illustration={byDesign(undefined, CalendarLaunchIllustration)}
            alt="Illustration of a user being restored to the workspace"
          />

          <ModalSheetContentTitle>Restore to workspace</ModalSheetContentTitle>

          <ModalSheetContentDescription>
            You are about to restore{' '}
            <b>
              {selectedUserName ??
                `${selectedUserCount} ${pluralize('user', selectedUserCount)}`}
            </b>{' '}
            to <b>{workspaceName}</b>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isRestorePending}
          />
          <Button
            label={
              selectedUserCount > 1
                ? 'Restore selected'
                : 'Restore to workspace'
            }
            variant="success"
            testId="user-management-restore-to-workspace-button"
            onPress={onRestore}
            isDisabled={isRestorePending}
            isPending={isRestorePending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
